// =============================================================================
// features/sales/components/OutcomeSelector.tsx — Seletor de desfecho
// comercial (`SalesOutcomeCode`), componente do módulo (doc 26 §7).
//
// Fonte única de rótulos/regras: `SALES_OUTCOME_META` (F32-S01,
// packages/shared-schemas/src/sales.ts) — este componente NUNCA duplica a
// regra de "exige observação"/"exige retorno", só lê os metadados e informa
// o formulário chamador via `onChange`.
//
// `options` (F34-S05, doc 26 §22 "a lista de desfechos vem de
// GET /api/sales/outcomes, não de constante no frontend"): quando o chamador
// passa `options` (ex: `RegisterContactDialog` lendo `useEnabledOutcomes()`),
// o seletor usa EXATAMENTE essa lista — já filtrada pelos desfechos que a
// organização habilitou e com o `label`/`requiresNote`/`requiresAppointment`
// EFETIVOS (que podem ter sido customizados via `PUT /api/sales/outcomes`,
// divergindo do default de `SALES_OUTCOME_META`). Omitir `options` preserva o
// comportamento histórico (F33-S06): catálogo canônico completo, usado hoje
// pela agenda (`CompleteAppointmentForm.tsx`, fora de `files_allowed` deste
// slot) — nenhum consumidor existente quebra.
// =============================================================================

import type { SalesOutcomeCode } from '@elemento/shared-schemas';
import { SALES_OUTCOME_CODES, SALES_OUTCOME_META } from '@elemento/shared-schemas';
import * as React from 'react';

import { Select } from '../../../components/ui/Select';

export interface OutcomeOption {
  value: SalesOutcomeCode;
  label: string;
  requiresNote: boolean;
  requiresAppointment: boolean;
}

export interface OutcomeSelectorProps {
  id: string;
  value: SalesOutcomeCode | '';
  onChange: (value: SalesOutcomeCode) => void;
  error?: string | undefined;
  disabled?: boolean;
  required?: boolean;
  /**
   * Catálogo EFETIVO de desfechos (já filtrado por habilitado + metadados da
   * organização). Omitir usa o catálogo canônico completo (compat F33-S06).
   */
  options?: OutcomeOption[] | undefined;
}

const CANONICAL_OPTIONS: OutcomeOption[] = SALES_OUTCOME_CODES.map((code) => ({
  value: code,
  label: SALES_OUTCOME_META[code].label,
  requiresNote: SALES_OUTCOME_META[code].requiresNote,
  requiresAppointment: SALES_OUTCOME_META[code].requiresAppointment,
}));

/**
 * Select de desfecho comercial. O `hint` muda dinamicamente conforme o
 * outcome selecionado exige observação e/ou agendamento de retorno — lido
 * dos metadados EFETIVOS (`options`, quando informado) ou de
 * `SALES_OUTCOME_META` (default), a mesma fonte que o schema Zod usa para
 * validar no backend, então a UI nunca diverge da regra do servidor.
 */
export function OutcomeSelector({
  id,
  value,
  onChange,
  error,
  disabled,
  required,
  options,
}: OutcomeSelectorProps): React.JSX.Element {
  const catalog = options ?? CANONICAL_OPTIONS;
  const selected = value !== '' ? catalog.find((o) => o.value === value) : undefined;

  const hintParts: string[] = [];
  if (selected?.requiresNote) hintParts.push('exige observação');
  if (selected?.requiresAppointment) hintParts.push('exige agendar o retorno');
  const hint =
    hintParts.length > 0
      ? `Este desfecho ${hintParts.join(' e ')}.`
      : 'Selecione o resultado deste contato.';

  return (
    <Select
      id={id}
      label="Desfecho"
      required={required}
      placeholder="Selecione o desfecho"
      options={catalog.map((o) => ({ value: o.value, label: o.label }))}
      value={value}
      onChange={(e) => onChange(e.target.value as SalesOutcomeCode)}
      error={error}
      hint={error ? undefined : hint}
      disabled={disabled}
    />
  );
}
